import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControlLabel,
  FormHelperText,
  Stack,
  Switch,
} from '@mui/material';
import moment from 'moment-timezone';
import {
  CommunicationOutsideWindowBehavior,
  CommunicationWeekday,
  type CommunicationWindow as NotableCommunicationWindow,
} from 'notable-common';
import { type ReactNode, useId } from 'react';

import { Text, isDefined, isTruthy } from 'common';

import { CommunicationWindowSchedule } from './CommunicationWindowSchedule';
import { type CommunicationWindow } from './TimeRange';
import { useReactFlowViewContext } from '../../../../context/context';
import { BlockAutocomplete } from '../../../ConfigFields/Autocomplete';
import { BlockRadioGroupField } from '../../../ConfigFields/RadioGroupField';
import { useSelectedNodeField } from '../../../ConfigFields/useSelectedNodeField';

type OutsideWindowBehaviorVisibility = 'hidden' | 'selectable';

interface CommunicationWindowFormFieldsProps {
  expanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
  description: string;
  outsideWindowBehaviorVisibility?: OutsideWindowBehaviorVisibility;
  unrestrictedWarning?: ReactNode;
}

export const defaultCommunicationWindow: NotableCommunicationWindow = {
  daysOfWeek: [
    CommunicationWeekday.Monday,
    CommunicationWeekday.Tuesday,
    CommunicationWeekday.Wednesday,
    CommunicationWeekday.Thursday,
    CommunicationWeekday.Friday,
  ],
  timeRange: {
    start: { hours: 11, minutes: 0 },
    end: { hours: 21, minutes: 0 },
  },
  timeZone: 'America/New_York',
  outsideWindowBehavior: CommunicationOutsideWindowBehavior.Skip,
};

const outsideWindowBehaviorOptions = [
  { label: 'Skip (stop the flow)', value: CommunicationOutsideWindowBehavior.Skip },
  {
    label: 'Queue (wait and send at next valid window)',
    value: CommunicationOutsideWindowBehavior.Queue,
  },
];

export function CommunicationWindowFormFields(props: CommunicationWindowFormFieldsProps) {
  const {
    expanded,
    setIsExpanded,
    description,
    outsideWindowBehaviorVisibility = 'hidden',
    unrestrictedWarning,
  } = props;
  const id = useId();

  const {
    value: communicationWindow,
    setValue,
    visibleError,
  } = useSelectedNodeField<CommunicationWindow | undefined>('config.communicationWindow');
  const { isEditable } = useReactFlowViewContext();

  const fieldId = useId();
  const timeZoneNames = moment.tz.names();

  return (
    <Accordion
      variant="elevation"
      expanded={expanded}
      onChange={(_, isExpanded) => setIsExpanded(isExpanded)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        id={id}
        aria-controls="valid-windows-content"
      >
        <Text variant="heading5">Valid Windows</Text>
      </AccordionSummary>
      <AccordionDetails sx={{ gap: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Text variant="body2" sx={{ marginBottom: 2 }}>
              {description}
            </Text>
            <FormControlLabel
              label="Restrict to Valid Windows"
              control={
                <Switch
                  id={fieldId}
                  checked={isDefined(communicationWindow)}
                  disabled={!isEditable}
                  onChange={() => {
                    if (isDefined(communicationWindow)) {
                      setValue(undefined);
                    } else {
                      setValue(defaultCommunicationWindow);
                    }
                  }}
                />
              }
            />
          </Box>
          {!isDefined(communicationWindow) && unrestrictedWarning}
          {visibleError && (
            <FormHelperText error={isTruthy(visibleError)}>{visibleError}</FormHelperText>
          )}
          {isDefined(communicationWindow) && (
            <>
              <CommunicationWindowSchedule
                communicationWindow={communicationWindow}
                disabled={!isEditable}
                onUpdate={(updates) => setValue({ ...communicationWindow, ...updates })}
              />
              <BlockAutocomplete
                fieldName="config.communicationWindow.timeZone"
                label="Time Zone"
                options={timeZoneNames}
                getOptionLabel={(tz) => tz}
                freeSolo={false}
                multiple={false}
                required
              />
              {outsideWindowBehaviorVisibility === 'selectable' && (
                <BlockRadioGroupField
                  fieldName="config.communicationWindow.outsideWindowBehavior"
                  label="If outside of these hours"
                  options={outsideWindowBehaviorOptions}
                  defaultValue={CommunicationOutsideWindowBehavior.Skip}
                />
              )}
            </>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
